import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { toast } from 'react-hot-toast';
import { api } from '../../lib/api';
import { useTranslation } from 'react-i18next';

interface SaveTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  patientName: string;
  diagnosis: string;
  chiefComplaints?: string;
  clinicalNotes?: string;
  medications: any[];
  doctorId: string;
  clinicId: string;
}

const TEMPLATE_CATEGORIES = [
  { value: 'GENERAL', label: 'General' },
  { value: 'FEVER', label: 'Fever' },
  { value: 'COUGH_COLD', label: 'Cough & Cold' },
  { value: 'HYPERTENSION', label: 'Hypertension' },
  { value: 'DIABETES', label: 'Diabetes' },
  { value: 'ALLERGY', label: 'Allergy' },
  { value: 'GASTRIC', label: 'Gastric Issues' },
  { value: 'PAIN', label: 'Pain Management' },
  { value: 'INFECTION', label: 'Infection' },
  { value: 'OTHER', label: 'Other' },
];

export function SaveTemplateDialog({
  open,
  onClose,
  onSuccess,
  patientName,
  diagnosis,
  chiefComplaints,
  clinicalNotes,
  medications,
  doctorId,
}: SaveTemplateDialogProps) {
  const { t } = useTranslation('prescriptions');
  const [templateName, setTemplateName] = useState('');
  const [category, setCategory] = useState('GENERAL');
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Template name is required');
      return;
    }

    if (!diagnosis.trim()) {
      toast.error('Diagnosis is required to save as template');
      return;
    }

    if (medications.length === 0) {
      toast.error('At least one medication is required');
      return;
    }

    setLoading(true);
    try {
      const templatePayload = {
        name: templateName.trim(),
        category: category,
        chiefComplaint: chiefComplaints?.trim() || undefined,
        diagnosis: diagnosis.trim(),
        clinicalNotes: clinicalNotes?.trim() || undefined,
        medications: medications.map(med => ({
          medicationName: med.name,
          medicationMasterId: med.medicationMasterId || undefined,
          dosage: med.dosage,
          frequency: med.frequency,
          duration: med.duration,
          instructions: med.instructions,
        })),
        doctorId,
      };

      const response = await api.templates.create(templatePayload);

      if (response.data) {
        toast.success(`Template "${templateName}" saved successfully! ✓`);
        setTemplateName('');
        setCategory('GENERAL');
        onClose();
        onSuccess?.();
      } else {
        toast.error(response.error || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setTemplateName('');
      setCategory('GENERAL');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {t('savePrescriptionAsTemplate')}
      </DialogTitle>
      <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('saveThisPrescriptionAsATemplate')}
          </Typography>
        </Box>

        <TextField
          label={t('templateName')}
          placeholder={t('eGFeverWithCoughTreatment')}
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          fullWidth
          disabled={loading}
          autoFocus
          inputProps={{ maxLength: 100 }}
        />

        <FormControl fullWidth disabled={loading}>
          <InputLabel>{t('category')}</InputLabel>
          <Select
            value={category}
            label={t('category')}
            onChange={(e) => setCategory(e.target.value)}
          >
            {TEMPLATE_CATEGORIES.map(cat => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="caption" color="info.dark">
            <strong>{t('templateDetails')}</strong>
            <br />
            Patient: {patientName}
            <br />
            Diagnosis: {diagnosis}
            <br />
            Medications: {medications.length}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !templateName.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Saving...' : 'Save Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
